import definitions from "../definitions";
import TilemapLayer from "./TilemapLayer";
import Tileset from "./Tileset";

export default class Tilemap extends PIXI.Container {
    constructor() {
        super();

        this._columns = 0;
        this._rows = 0;
        this._width = 0;
        this._height = 0;
        this._tileWidth = 0;
        this._tileHeight = 0;

        this._layers = [];
        this._tileset = null;
    }

    /**
     * @param {string} path
     */
    static filenameFromPath(path) {
        let arr = path.split('/');
        arr = arr[arr.length - 1];
        return arr.split('.')[0];
    }

    /**
     * @param {string} name
     * @param {object} properties
     * @returns {undefined|*}
     */
    static getCustomProperty(name, properties) {
        for (const i in properties) {
            if (properties.hasOwnProperty(i)) {
                const property = properties[i];
                if (property.name === name) {
                    return property.value;
                }
            }
        }
        return undefined;
    }

    /**
     * @param {object} tilemapData
     * @param {object} collisionData
     */
    load(tilemapData, collisionData) {
        this.removeAllLayers();

        this._tileset = Tileset.create(tilemapData.tilesets);
        this._columns = tilemapData.width;
        this._rows = tilemapData.height;
        this._width = tilemapData.width * tilemapData.tilewidth;
        this._height = tilemapData.height * tilemapData.tileheight;
        this._tileWidth = tilemapData.tilewidth;
        this._tileHeight = tilemapData.tileheight;

        const layerArray = P3.utils.objectToArray(tilemapData.layers);
        for (let i = 0, count = layerArray.length; i < count; ++i) {
            const layerData = layerArray[i];
            if (layerData.type === definitions.TILEMAP_LAYER_TYPE.TILE) {
                this.addLayer(new TilemapLayer(
                    layerData.id,
                    layerData.name,
                    layerData.data,
                    layerData.width,
                    this._tileWidth,
                    this._tileHeight,
                    this._tileset,
                    collisionData
                ));
            }
        }
    }

    /**
     * @param {TilemapLayer} layer
     * @returns {boolean}
     */
    addLayer(layer) {
        if (this._layers.indexOf(layer) !== -1) {
            return false;
        }

        this.addChild(layer);
        this._layers.push(layer);
        return true;
    }

    /**
     * @param {TilemapLayer} layer
     * @returns {boolean}
     */
    removeLayer(layer) {
        const index = this._layers.indexOf(layer);
        if (index === -1) {
            return false;
        }

        this.removeChild(layer);
        this._layers.splice(index, 1);
        return true;
    }

    removeAllLayers() {
        for (let i = 0; i < this._layers.length; ++i) {
            const layer = this._layers[i];
            this.removeChild(layer);
        }
        this._layers.length = 0;
    }

    /**
     * @param {number} tx
     * @param {number} ty
     * @param {number} columns
     * @param {number} rows
     */
    updateBounds(tx, ty, columns, rows) {
        for (let i = 0, count = this._layers.length; i < count; ++i) {
            const layer = this._layers[i];
            layer.updateBounds(tx, ty, columns, rows);
        }
    }

    /**
     * @param {PIXI.Rectangle} rect
     */
    queryRect(rect) {
        let tiles = [];

        for (let i = 0, count = this._layers.length; i < count; ++i) {
            const layer = this._layers[i];
            tiles = tiles.concat(layer.queryRect(rect));
        }
        return tiles;
    }

    /**
     * @param {number} x
     * @param {number} y
     * @returns {PIXI.Point}
     */
    toTile(x, y) {
        return new PIXI.Point(Math.round(x / this._tileWidth), Math.round(y / this._tileHeight));
    }

    /**
     * @param {number} tx
     * @param {number} ty
     * @returns {PIXI.Point}
     */
    fromTile(tx, ty) {
        return new PIXI.Point(tx * this._tileWidth, ty * this._tileHeight);
    }

    /**
     * @returns {P3.Camera}
     */
    get camera() {
        return this._camera;
    }

    /**
     * @returns {number}
     */
    get columns() {
        return this._columns;
    }

    /**
     * @returns {number}
     */
    get rows() {
        return this._rows;
    }

    /**
     * @returns {number}
     */
    get width() {
        return this._width;
    }

    /**
     * @returns {number}
     */
    get height() {
        return this._height;
    }

    /**
     * @returns {number}
     */
    get tileWidth() {
        return this._tileWidth;
    }

    /**
     * @returns {number}
     */
    get tileHeight() {
        return this._tileHeight;
    }

    /**
     * @returns {Tileset}
     */
    get tileset() {
        return this._tileset;
    }
}